import { Link } from "react-router-dom";
import { testimonials } from "../../../mocks/testimonials";

interface TestimonialsProps {
  heading?: string;
}

export default function TestimonialsSection({
  heading = "What Customers Are Saying",
}: TestimonialsProps) {
  const [featured, ...rest] = testimonials;

  return (
    <section className="bg-[#1A1A1A] py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-14">
          <span className="text-[#10B981] font-body font-600 text-sm tracking-widest uppercase mb-3 block">
            Testimonials
          </span>
          <h2 className="font-headline font-800 text-white text-3xl lg:text-4xl xl:text-5xl mb-4 leading-tight">
            {heading}
          </h2>
          <div className="flex items-center justify-center gap-1.5 mb-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="w-5 h-5 flex items-center justify-center">
                <i className="ri-star-fill text-amber-400 text-lg"></i>
              </div>
            ))}
          </div>
          <span className="text-white/50 font-body text-sm">5.0 Average — 100+ Reviews from Central Florida Homeowners</span>
        </div>

        {/* Featured testimonial */}
        <div className="bg-white/5 border border-[#10B981]/30 rounded-lg p-8 lg:p-10 mb-6 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-[#10B981]"></div>
          <div className="flex gap-1 mb-5">
            {[1,2,3,4,5].map(i => (
              <div key={i} className="w-4 h-4 flex items-center justify-center">
                <i className="ri-star-fill text-amber-400 text-sm"></i>
              </div>
            ))}
          </div>
          <p className="text-white font-body text-lg lg:text-xl leading-relaxed mb-6 italic max-w-4xl">
            &ldquo;{featured.text}&rdquo;
          </p>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#10B981]">
              <i className="ri-user-fill text-white text-base"></i>
            </div>
            <div>
              <span className="font-body font-700 text-white text-sm block">{featured.name}</span>
              <span className="text-white/50 font-body text-xs">{featured.location} — Verified Customer</span>
            </div>
          </div>
        </div>

        {/* Remaining testimonial cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
          {rest.slice(0, 5).map((t) => (
            <div
              key={t.id}
              className="bg-white/5 rounded-lg p-6 border border-white/10 hover:border-[#10B981]/40 transition-all duration-200"
            >
              <div className="flex gap-0.5 mb-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-amber-400 text-sm"></i>
                  </div>
                ))}
              </div>
              <p className="text-white/80 font-body text-sm leading-relaxed mb-5 italic">
                &ldquo;{t.text}&rdquo;
              </p>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#10B981]/20">
                  <i className="ri-user-fill text-[#10B981] text-sm"></i>
                </div>
                <div>
                  <span className="font-body font-600 text-white text-sm block">{t.name}</span>
                  <span className="text-white/40 font-body text-xs">{t.location}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA below testimonials */}
        <div className="text-center border-t border-white/10 pt-10">
          <p className="text-white/70 font-body text-base mb-5">
            Join hundreds of Central Florida homeowners who trust Thomas Roofing with their most important investment.
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Link
              to="/#contact"
              className="bg-[#10B981] hover:bg-emerald-500 text-white font-body font-700 text-sm px-8 py-3.5 rounded-md cursor-pointer whitespace-nowrap transition-colors"
            >
              Get My Free Inspection
            </Link>
            <a
              href="tel:407-408-0577"
              className="flex items-center gap-2 text-white/70 hover:text-white font-body font-600 text-sm cursor-pointer whitespace-nowrap transition-colors"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-fill text-[#10B981] text-sm"></i>
              </div>
              Call (407) 408-0577
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
